import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded'
import LanRoundedIcon from '@mui/icons-material/LanRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded'
import WorkRoundedIcon from '@mui/icons-material/WorkRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import ArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import TextSnippetRoundedIcon from '@mui/icons-material/TextSnippetRounded'
import { Button, Grid, Stack, Tab } from '@mui/material'
import Breadcrumbs from 'components/Breadcrumbs'
import Label from 'components/Label'
import { FormTabsProps, useTab } from 'hooks/tab.hook'
import Layout from 'layouts/Default'
import { TabEducation } from './components/TabEducation'
import { TabFamily } from './components/TabFamily'
import { TabHealth } from './components/TabHealth'
import { TabWelcome } from 'pages/TeenagersEdit/components/TabWelcome'
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import teenagerService from 'services/teenager.service'
import { HealthIcon } from '../../assets/icons/HealthIcon'
import TabContent from './components/Record/components/TabContent'
import { TabFamiliarReference } from './components/TabFamiliarReference'
import { TabPersonalData } from './components/TabPersonalData'
import { TabProfessionalData } from './components/TabProfessionalData'
import { TabRelatedProcesses } from 'pages/TeenagersEdit/components/TabRelatedProcesses'
import { StyledBoxContainer, StyledTabs as Tabs, StyledTabsContent as TabsContent } from './styles'
import { TeenagerType } from 'types/teenager-record.type'
import { useTeenagerPictures } from 'hooks/teenager-pictures.hook'
import { Form } from 'components/Form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SystemMessages } from 'utils/enums/system-message.enum'
import {
  hearingIdentificationInit,
  hearingIdentificationSchema,
  HearingIdentificationType,
} from 'types/hearing-identification.type'
import UploadPicture from 'components/UploadPicture'
import DialogAlert from 'components/Dialog/Alert'
import {
  HearingIdentificationBlockFieldsProvider,
  useHearingIdentificationBlockFieldsContext,
} from '../../contexts/hearing-identification-block-fields.context'
import { useSnackBar } from '../../contexts/snackbar.context'
import { HttpErrorType } from '../../types/http.type'
import { useBackdrop } from '../../contexts/backdrop.context'
import { errorColor, errorMessage } from 'utils/error.util'
import { UserInfo } from 'pages/TeenagersRecord/components/Record/components/UserInfo'
import { base64ToFile, obterTamanhoArquivo } from 'utils/base64.utils'

export type TabTeenagerProps = {
  data?: TeenagerType
}

export const TeenagersEditPage = () => {
  return (
    <HearingIdentificationBlockFieldsProvider>
      <InternalComponent />
    </HearingIdentificationBlockFieldsProvider>
  )
}

const InternalComponent: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { handleUnblockAllFields } = useHearingIdentificationBlockFieldsContext()

  const [data, setData] = useState<TeenagerType | undefined>(undefined)
  const [openDialogConfirm, setOpenDialogConfirm] = useState(false)
  const [picture, setPicture] = useState<ChangeEvent<HTMLInputElement>>(
    {} as ChangeEvent<HTMLInputElement>,
  )

  const tabs: FormTabsProps[] = [
    {
      index: 0,
      label: 'Dados pessoais',
      icon: <PersonRoundedIcon color='primary' />,
      content: <TabPersonalData data={data} />,
    },
    {
      index: 1,
      label: 'Processos relacionados',
      icon: <TextSnippetRoundedIcon color={'primary'} />,
      content: <TabRelatedProcesses />,
    },
    {
      index: 2,
      label: 'Saúde',
      icon: <HealthIcon color='primary' />,
      content: <TabHealth data={data} />,
    },
    {
      index: 3,
      label: 'Educação',
      icon: <SchoolRoundedIcon color='primary' />,
      content: <TabEducation data={data} />,
    },
    {
      index: 4,
      label: 'Dados profissionais',
      icon: <WorkRoundedIcon color='primary' />,
      content: <TabProfessionalData data={data} />,
    },
    {
      index: 5,
      label: 'Família',
      icon: <FamilyRestroomRoundedIcon color='primary' />,
      content: <TabFamily data={data} />,
    },
    {
      index: 6,
      label: 'Acolhimento',
      icon: <VolunteerActivismRoundedIcon color='primary' />,
      content: <TabWelcome data={data} />,
    },
    {
      index: 7,
      label: 'Referência familiar',
      icon: <LanRoundedIcon color='primary' />,
      content: <TabFamiliarReference />,
    },
  ]

  const {
    tab: { current },
    handleChange,
  } = useTab(tabs)

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const fillTeenagerData = useCallback(async (): Promise<void> => {
    try {
      if (!id) return
      handleBackdropOpen()
      const [teenagerDataRes] = await Promise.all([
        teenagerService.getById(parseInt(id)).then((res) => res.data),
      ]).finally(() => handleBackdropClose())

      setData(teenagerDataRes)
    } catch (err) {
      console.error('Teenager Info Requests: ', err)
    }
  }, [id])

  const handleBackTeenagerRecords = (): void => navigate(`/consultar-adolescente`)

  useEffect(() => {
    fillTeenagerData()
  }, [fillTeenagerData])

  const methods = useForm<HearingIdentificationType>({
    mode: 'all',
    resolver: zodResolver(hearingIdentificationSchema),
    defaultValues: hearingIdentificationInit,
  })

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = methods

  const handleSaveSubmit = async (values: any) => {
    if (!id) return
    handleBackdropOpen()
    await teenagerService
      .update(parseInt(id), values)
      .then(async () => {
        showSnackBar(SystemMessages.MESSAGE_001, 'success')
      })
      .catch((error: HttpErrorType) => {
        showSnackBar(errorMessage(error), errorColor(error))
      })
      .finally(() => fillTeenagerData().finally(() => handleBackdropClose()))
  }

  const foto = watch('foto')

  const imagePicture = useMemo(
    () => base64ToFile(foto?.arquivoBase64, foto?.arquivoNome, foto?.arquivoTipo),
    [foto?.arquivoBase64, foto?.arquivoNome, foto?.arquivoTipo],
  )

  const handleChangePicture = async (e: ChangeEvent<HTMLInputElement>) => {
    const allowedExtensions = ['image/png', 'image/bmp', 'image/tiff', 'image/jpeg', 'image/jpg']
    const maxSizeInMB = 10 // Tamanho máximo permitido em MB
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024 // Converte para bytes

    const file = e.target.files?.[0]
    if (file) {
      // Verifica se o tipo de arquivo é permitido
      if (!allowedExtensions.includes(file.type)) {
        showSnackBar('Apenas arquivos .png, .bmp, .tiff, .jpg, .jpeg são permitidos.', 'warning')
        return
      }

      if (file.size > maxSizeInBytes) {
        showSnackBar(`O arquivo excede o tamanho máximo permitido de ${maxSizeInMB} MB.`, 'error')
        return
      }

      // Converte a imagem para base64
      const reader = new FileReader()
      reader.onloadend = () => {
        // Aqui o reader.result vai conter a imagem em base64
        let base64Image = reader.result as string

        if (base64Image.includes(',')) {
          base64Image = base64Image.split(',')[1]
        }

        // Atualiza o valor no Hook Form
        setValue('foto.id', foto?.id || null, { shouldDirty: true })
        setValue('foto.arquivoBase64', base64Image, { shouldDirty: true })
        setValue('foto.arquivoNome', file.name, { shouldDirty: true })
        setValue('foto.arquivoTipo', file.type, { shouldDirty: true })
      }
      reader.readAsDataURL(file) // Inicia a leitura do arquivo
    }
  }

  const handleResetPicture = async () => {
    setValue('foto', null, { shouldDirty: true })
  }

  useEffect(() => {
    if (id) {
      handleUnblockAllFields()
    }
  }, [id])

  useEffect(() => {
    if (data?.identAdolescente) {
      console.log('data?.identAdolescente', data?.identAdolescente)
      reset(data?.identAdolescente)
    }
  }, [data])

  useEffect(() => {
    console.log('errors', errors)
    console.log(
      'dataInicioJovemAprendiz',
      watch('identificacaoAdolescente.dataInicioJovemAprendiz'),
    )
    console.log('dataNscimento', watch('dataNascimento'))
  }, [errors])

  return (
    <Layout>
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Breadcrumbs>
            <div onClick={handleBackTeenagerRecords} style={{ cursor: 'pointer' }}>
              <Label text='Consultar Adolescente' />
            </div>
            <Label text='Ficha Adolescente' bold />
          </Breadcrumbs>
        </Grid>
        <Grid item xs={12}>
          {data && (
            <Form methods={methods} onSubmit={handleSubmit(handleSaveSubmit)}>
              <Grid container spacing={2}>
                <Grid item lg={3} md={4} xs={12}>
                  <StyledBoxContainer>
                    <Stack direction='column' spacing={2} sx={{ textAlign: 'center' }}>
                      <UploadPicture
                        image={imagePicture}
                        handleChange={handleChangePicture}
                        handleReset={handleResetPicture}
                        metadados={{
                          nomeArquivo: foto?.arquivoNome,
                          tipoArquivo: foto?.arquivoTipo,
                          tamanho: obterTamanhoArquivo(foto?.arquivoBase64),
                        }}
                      />
                      {data && (
                        <UserInfo data={{ ...data, identAdolescente: data.identAdolescente }} />
                      )}
                    </Stack>
                  </StyledBoxContainer>
                </Grid>
                <Grid item lg={9} md={8} xs={12}>
                  <Stack direction='column' spacing={2}>
                    <StyledBoxContainer>
                      <Tabs value={current} onChange={handleChange}>
                        {tabs.map((item) => (
                          <Tab
                            key={item.index}
                            value={item.index}
                            label={item.label}
                            icon={item.icon}
                            iconPosition='start'
                          />
                        ))}
                      </Tabs>
                    </StyledBoxContainer>
                    <TabsContent>
                      {tabs.map((item) => (
                        <TabContent key={item.index} value={current} index={item.index}>
                          {item.content}
                        </TabContent>
                      ))}
                    </TabsContent>
                  </Stack>
                </Grid>
              </Grid>
              <Stack
                display='flex'
                direction='row'
                justifyContent='center'
                spacing={2}
                sx={{ pt: 2, pb: 2 }}
              >
                <Button
                  variant='contained'
                  startIcon={<ArrowLeftRoundedIcon />}
                  color='secondary'
                  onClick={() => navigate(-1)}
                  sx={{ pl: 2, pr: 2 }}
                >
                  Voltar
                </Button>

                <Button
                  type='submit'
                  variant='contained'
                  startIcon={<SaveRoundedIcon />}
                  color='info'
                  sx={{ pl: 2, pr: 2 }}
                >
                  Salvar
                </Button>
              </Stack>
            </Form>
          )}
        </Grid>
      </Grid>
      <DialogAlert
        title=''
        content={
          <Stack direction='column' spacing={1}>
            <Label text='Tem certeza que deseja alterar a foto do adolescente?  ' bold />
          </Stack>
        }
        open={openDialogConfirm}
        onSuccess={() => {
          handleChangePicture(picture)
          setOpenDialogConfirm(false)
        }}
        onClose={() => setOpenDialogConfirm(false)}
        buttonSuccesText='Sim'
        buttonCloseText='Não'
      />
    </Layout>
  )
}
